import { delay } from '@utils/common';
import { getGraphQLErrorMessage } from '@utils/graphQLError';

/** Matches form-service FormPreparationLock contention error. */
export const PREPARE_FORM_COMPLETIONS_LOCK_MESSAGE = /already in progress/i;

/** Wait between lock retries while another prepare holds the lock. */
export const PREPARE_LOCK_RETRY_DELAY_MS = 3_000;

/**
 * Enough attempts to cover a multi-minute in-flight prepare
 * (~6 minutes of delays, matching form-service lock TTL).
 */
export const PREPARE_LOCK_RETRY_MAX_ATTEMPTS = 120;

/** Thrown when the caller cancels lock retries (e.g. step unmount). */
export class PrepareFormCompletionsRetryCancelled extends Error {
  constructor() {
    super('Prepare form completions lock retry cancelled');
    this.name = 'PrepareFormCompletionsRetryCancelled';
  }
}

export const isPrepareFormCompletionsRetryCancelled = (
  error: unknown,
): boolean => error instanceof PrepareFormCompletionsRetryCancelled;

export const isPrepareFormCompletionsLockError = (error: unknown): boolean => {
  const message =
    getGraphQLErrorMessage(error) ??
    (error instanceof Error ? error.message : String(error ?? ''));
  return PREPARE_FORM_COMPLETIONS_LOCK_MESSAGE.test(message);
};

type PrepareFormCompletionsWithLockRetryOptions = {
  maxAttempts?: number;
  delayMs?: number;
  wait?: (ms: number) => Promise<void>;
  isCancelled?: () => boolean;
};

const throwIfCancelled = (isCancelled?: () => boolean): void => {
  if (isCancelled?.()) {
    throw new PrepareFormCompletionsRetryCancelled();
  }
};

/**
 * Runs prepareFormCompletions, and if another prepare already holds the
 * workflow lock, keeps waiting and retrying instead of surfacing a hard error.
 * Non-lock failures (and exhausted lock retries) are rethrown for the UI.
 * Callers can stop the loop via `isCancelled` (e.g. on unmount).
 */
export const prepareFormCompletionsWithLockRetry = async (
  runPrepare: () => Promise<unknown>,
  {
    maxAttempts = PREPARE_LOCK_RETRY_MAX_ATTEMPTS,
    delayMs = PREPARE_LOCK_RETRY_DELAY_MS,
    wait = delay,
    isCancelled,
  }: PrepareFormCompletionsWithLockRetryOptions = {},
): Promise<void> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    throwIfCancelled(isCancelled);
    try {
      await runPrepare();
      return;
    } catch (error) {
      if (isPrepareFormCompletionsRetryCancelled(error)) {
        throw error;
      }
      const canRetryLock =
        isPrepareFormCompletionsLockError(error) && attempt < maxAttempts;
      if (!canRetryLock) {
        throw error;
      }
      await wait(delayMs);
      throwIfCancelled(isCancelled);
    }
  }
};
